import axios from 'axios';
import baseApi from '@/shared/services/bright_finder';
import deepmerge from 'deepmerge';
import { asyncApi, handleError } from '@/shared/services/api';

axios.defaults.withCredentials = true;
axios.defaults.headers.get.Accept = 'application/json';
axios.defaults.headers.patch.Accept = 'application/json';
axios.defaults.headers.post.Accept = 'application/json';
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.patch['Content-Type'] = 'application/json';

const adminApi = {
  admin: {
    api_key: {
      baseUrl(id) {
        let url = `${window.api_url}/admin/api_keys`;
        if (id) {
          url += `/${id}`;
        }
        return url;
      },

      create(apiKey, success, failure) {
        axios.post(this.baseUrl(), { api_key: apiKey }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(params, success) {
        axios.get(this.baseUrl(), { params }).then(success);
      },

      update(id, apiKey, success, failure) {
        axios.patch(this.baseUrl(id), { api_key: apiKey }).then(success).catch(failure);
      },
    },

    api_permission: {
      baseUrl() {
        return `${window.api_url}/admin/api_permissions`;
      },

      index(success) {
        axios.get(this.baseUrl()).then(success);
      },
    },

    course_group: {
      baseUrl(id) {
        let url = `${window.api_url}/admin/course_groups`;
        if (id) {
          url += `/${id}`;
        }
        return url;
      },

      create(course_group, success, failure) {
        axios.post(this.baseUrl(), { course_group }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(success) {
        axios.get(this.baseUrl()).then(success);
      },

      update(id, course_group, success, failure) {
        axios.patch(this.baseUrl(id), { course_group }).then(success).catch(failure);
      },
    },

    member: {
      baseUrl() {
        return `${window.api_url}/admin/members`;
      },

      create(member, success, failure) {
        axios.post(this.baseUrl(), { member }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      get(id, success, failure) {
        axios.get(`${this.baseUrl()}/${id}`).then(success).catch(failure);
      },

      index(success, failure) {
        axios.get(this.baseUrl()).then(success).catch(failure);
      },

      update(id, member, success, failure) {
        if (!success) return axios.patch(`${this.baseUrl()}/${id}`, { member });

        return axios.patch(`${this.baseUrl()}/${id}`, { member }).then(success).catch(failure);
      },
    },

    organization: {
      base_url() {
        return `${window.api_url}/admin/organization`;
      },

      get(success) {
        if (success) {
          return axios.get(this.base_url()).then(success);
        }
        return axios.get(this.base_url());
      },

      update(organization, success, failure) {
        if (success) {
          return axios.patch(this.base_url(), { organization }).then(success).catch(failure);
        }
        return axios.patch(this.base_url(), { organization }).catch(handleError);
      },
    },
  },

  organization: {
    brand: asyncApi({
      endpoint: '/organization/brands',
      payloadKey: 'brand',
    }),

    dashboard: asyncApi({
      endpoint: '/organization/dashboards',
      payloadKey: 'dashboard',
    }),

    invoiceBatch: {
      ...asyncApi({
        endpoint: '/organization/invoice_batches',
        payloadKey: 'invoice_batch',
      }),
      async approve(id, successCallback = null, failureCallback = null) {
        return axios
          .patch(`${this.baseUrl(id)}/approve`, this.createPayload({ id }))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) failureCallback(error);
            return handleError(error);
          });
      },
      async removeInvoices(id, invoice_ids, successCallback = null, failureCallback = null) {
        return axios
          .patch(`${this.baseUrl(id)}/remove_invoices`, this.createPayload({ invoice_ids }))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) failureCallback(error);
            return handleError(error);
          });
      },
    },

    metric: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/metrics/${id}`;
        }
        return `${window.api_url}/organization/metrics`;
      },

      create(metric, success, failure) {
        axios.post(this.baseUrl(), metric).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(id, metric, success, failure) {
        axios.patch(this.baseUrl(id), { metric }).then(success).catch(failure);
      },
    },

    profile: {
      base_url() {
        return `${window.api_url}/organization/profile`;
      },

      get(success) {
        const params = {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          withCredentials: true,
        };
        if (!success) return axios.get(this.base_url(), params).catch(handleError);
        return axios.get(this.base_url(), params).then(success);
      },

      update(profile, success, failure) {
        axios.patch(this.base_url(), { profile }).then(success).catch(failure);
      },
    },

    question: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/questions/${id}`;
        }
        return `${window.api_url}/organization/questions`;
      },

      create(question, success, failure) {
        axios.post(this.baseUrl(), question).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(params, success) {
        axios.get(this.baseUrl(), { params }).then(success);
      },

      update(id, question, success, failure) {
        if (!success) return axios.patch(this.baseUrl(id), { question }).catch(handleError);

        return axios.patch(this.baseUrl(id), { question }).then(success).catch(failure);
      },
    },
  },

  upload: {
    baseUrl() {
      return `${window.api_url}/organization/uploads`;
    },

    create(upload, success, failure) {
      axios.post(this.baseUrl(), { upload }).then(success).catch(failure);
    },
  },

  registration: {
    create(registration, success, failure) {
      axios.post(`${window.api_url}/registrations`, { registration }).then(success).catch(failure);
    },
  },
};

export default deepmerge.all([baseApi, adminApi]);
